import type { ButtonVariant } from '@/components/common/Button';

const fontColour = {
  navy: 'white',
  white: 'deep-space',
  indigo: 'white',
  'deep-space': 'white',
  ultraviolet: 'white',
  electron: 'white',
  default: 'deep-space',
};

const buttonColours = {
  navy: {
    border: 'white',
    backgroundColour: 'transparent',
    backgroundOnHover: 'white',
  },
  indigo: {
    border: 'white',
    backgroundColour: 'transparent',
    backgroundOnHover: 'white',
  },
  'deep-space': {
    border: 'white',
    backgroundColour: 'transparent',
    backgroundOnHover: 'white',
  },
  ultraviolet: {
    border: 'white',
    backgroundColour: 'transparent',
    backgroundOnHover: 'white',
  },
  white: {
    border: 'deep-space',
    backgroundColour: 'transparent',
    backgroundOnHover: 'deep-space',
  },
  default: {
    border: 'deep-space',
    backgroundColour: 'transparent',
    backgroundOnHover: 'deep-space',
  },
};

type ButtonVariantOptions = {
  backgroundColour: string;
  fontColour: string;
  backgroundDisabled?: string;
  disabledFontColour: string;
  backgroundOnHover?: string;
  underlineOnHover?: 'underline';
};

interface ButtonColors {
  [key: string]: {
    [key in ButtonVariant]: ButtonVariantOptions;
  };
}

export const buttonColoursV2: ButtonColors = {
  white: {
    solid: {
      backgroundColour: 'deep-space',
      backgroundOnHover: 'deep-space-hover',
      backgroundDisabled: 'deep-space-disabled',
      fontColour: 'white',
      disabledFontColour: 'white',
    },
    text: {
      backgroundColour: 'transparent',
      backgroundDisabled: 'transparent',
      underlineOnHover: 'underline',
      fontColour: 'deep-space',
      disabledFontColour: 'deep-space-disabled',
    },
    default: {
      backgroundColour: 'deep-space',
      backgroundOnHover: 'deep-space-hover',
      backgroundDisabled: 'deep-space-disabled',
      fontColour: 'white',
      disabledFontColour: 'white',
    },
  },
  'deep-space': {
    solid: {
      backgroundColour: 'white',
      backgroundOnHover: 'white',
      backgroundDisabled: 'deep-space-disabled',
      fontColour: 'deep-space',
      disabledFontColour: 'white',
    },
    text: {
      backgroundColour: 'transparent',
      backgroundDisabled: 'transparent',
      underlineOnHover: 'underline',
      fontColour: 'white',
      disabledFontColour: 'deep-space-disabled',
    },
    default: {
      backgroundColour: 'white',
      backgroundOnHover: 'deep-space-hover',
      backgroundDisabled: 'deep-space-disabled',
      fontColour: 'deep-space',
      disabledFontColour: 'white',
    },
  },
  'inverse-subtle': {
    solid: {
      backgroundColour: 'inverse-base',
      backgroundOnHover: 'white',
      backgroundDisabled: 'inverse-disabled',
      fontColour: 'deep-space',
      disabledFontColour: 'deep-space',
    },
    text: {
      backgroundColour: 'transparent',
      backgroundDisabled: 'transparent',
      underlineOnHover: 'underline',
      fontColour: 'white',
      disabledFontColour: 'deep-space-disabled',
    },
    default: {
      backgroundColour: 'inverse-subtle-default',
      backgroundOnHover: 'inverse-subtle-hover',
      backgroundDisabled: 'inverse-subtle-disabled',
      fontColour: 'white-dwarf',
      disabledFontColour: 'white-dwarf',
    },
  },
  'inverse-subtle-dark': {
    solid: {
      backgroundColour: 'inverse-base',
      backgroundOnHover: 'white',
      backgroundDisabled: 'inverse-disabled',
      fontColour: 'deep-space',
      disabledFontColour: 'deep-space',
    },
    text: {
      backgroundColour: 'transparent',
      backgroundDisabled: 'transparent',
      underlineOnHover: 'underline',
      fontColour: 'white',
      disabledFontColour: 'deep-space-disabled',
    },
    default: {
      backgroundColour: 'inverse-subtle-dark-default',
      backgroundOnHover: 'inverse-subtle-dark-hover',
      backgroundDisabled: 'inverse-subtle-dark-disabled',
      fontColour: 'deep-space',
      disabledFontColour: 'deep-space-700',
    },
  },
};

export const actionButtonColours = {
  light: {
    flat: {
      backgroundColour: 'space-200',
      backgroundOnHover: 'action-subtle-hover',
      fontColour: 'deep-space',
    },
    text: {
      backgroundColour: 'transparent',
      backgroundDisabled: 'transparent',
      backgroundOnHover: 'transparent',
      fontColour: 'deep-space',
    },
  },
  dark: {
    flat: {
      backgroundColour: 'inverse-subtle-default',
      backgroundOnHover: 'inverse-subtle-hover',
      fontColour: 'inverse-base',
    },
    text: {
      backgroundColour: 'transparent',
      backgroundDisabled: 'transparent',
      backgroundOnHover: 'transparent',
      fontColour: 'inverse-base',
      disabledFontColour: 'deep-space-disabled',
    },
  },
};

const navbarThemeColour = {
  navy: 'light',
  indigo: 'light',
  'deep-space': 'light',
  ultraviolet: 'light',
  white: 'dark',
  default: 'dark',
} as const;

type NavBarInputs = keyof typeof navbarThemeColour;

export const complementaryFont = (backgroundColour) => {
  return fontColour[backgroundColour] || fontColour.default;
};

export const complementaryButton = (backgroundColour) => {
  return buttonColours[backgroundColour] || buttonColours.default;
};

export const complementaryNavbar = (backgroundColour: NavBarInputs) => {
  return navbarThemeColour[backgroundColour] || navbarThemeColour.default;
};
